import {
  answerKindsOf,
  composition,
  findLevel,
  findTopic,
  topicsBySection,
  type OgeTopic,
  type PickProblem,
  type TopicPickRow,
} from "@edu/shared";
import { exerciseCountText, fromGeneratorsText } from "../../../lib/plurals.ts";
import { LevelPicker } from "./LevelPicker.tsx";
import { onlyLevelsNote, singleLevelLine } from "./one-topic-text.ts";
import { materialLine, type TopicSubject } from "./subject.ts";
import { rowProblemText, type TopicPicksPlace } from "./topic-picks-text.ts";

/**
 * Одна строка-пик: тема, уровень, количество (5.5-5.6 docs/homework-phase2-design.md).
 *
 * ⚠️ Порядок ручек в строке не случайный: сначала тема, потом уровень. Проект
 * (5.6, правило 3) разбирает и обратный порядок, где сначала выбирают уровень, и
 * требует к нему счётчик «тем без сложного уровня скрыто: 10», потому что
 * молча короткий список тем это враньё о продукте. Здесь список тем не сужается
 * никогда, значит и прятать нечего: правило снято не отменой, а тем, что случай
 * не наступает.
 *
 * ⚠️ Текст проблемы строки живёт в `topic-picks-text.ts`: часть слов зависит от
 * места (домашка или урок), а проверить слова можно только в чистой функции.
 */

/**
 * Состав словами. ⚠️ Строка нужна ровно затем, чтобы «3 задания среднего уровня
 * по корням» перестало быть котом в мешке: состав фиксируется в момент создания
 * и не правится никогда.
 */
function compositionLine(
  parts: ReturnType<typeof composition>, count: number, merged: boolean,
): string {
  const names = parts.map((p) => `«${p.title}» (${p.count})`).join(", ");
  // «1 задание соберутся» это машинный текст ровно там, где преподаватель
  // впервые пробует ветку и берёт одно задание
  const verb = count % 10 === 1 && count % 100 !== 11 ? "соберётся" : "соберутся";
  const head = `${exerciseCountText(count)} ${verb} ${fromGeneratorsText(parts.length)}: ${names}`;
  // ⚠️ Состав считается по СЛИТОМУ количеству (`mergedCount`), иначе две строки
  // «Корень, 5» пообещали бы 3 и 2 каждая, а сервер разложил бы десять как 5 и
  // 5. Раз число в строке чужое, про это надо сказать вслух
  return merged ? `Всего по этой теме ${head}` : head;
}

/**
 * Форма ответа. ⚠️ Смешанный состав это не крайний случай: у корней среднего
 * уровня один генератор спрашивает вводом числа, другой выбором из вариантов, и
 * четыре задания приедут ученику двумя парами разной формы. Молчать про это
 * нельзя по той же причине, по которой нельзя молчать про «5 заданий из 1
 * генератора»: преподаватель ожидал однородности и узнает о ней от ученика.
 */
function answerLine(parts: ReturnType<typeof composition>): string {
  const kinds = answerKindsOf(parts);
  if (!kinds.mixed) {
    return kinds.number > 0 ? "Ответ: ввод числа" : "Ответ: выбор из вариантов";
  }
  return `⚠ Форма ответа разная: ${kinds.number} с вводом числа, ${kinds.choice} с выбором из вариантов`;
}

export function TopicRow(props: {
  /** Предмет справочника: от него зависит, чем читается `grade` у темы. */
  subject: TopicSubject;
  /** Место строк, из состояния `TopicPicks`: от него зависит часть слов. */
  place: TopicPicksPlace;
  topics: readonly OgeTopic[];
  row: TopicPickRow;
  /**
   * Сколько заданий по этой теме и уровню закажется ВСЕМИ строками вместе
   * (`mergedCount`). Состав считается по нему, а не по количеству строки:
   * сервер сложит строки в один пик до прогона генераторов.
   */
  merged: number;
  max: number;
  problem: PickProblem | null;
  removable: boolean;
  busy: boolean;
  onSkill: (skill: string) => void;
  onDifficulty: (difficulty: number) => void;
  onCount: (count: number) => void;
  onRemove: () => void;
}) {
  const { row } = props;
  const topic = row.skill === "" ? null : findTopic(props.topics, row.skill);
  const level = topic === null ? null : findLevel(topic, row.difficulty);
  const available = topic === null ? [] : topic.levels.map((l) => l.difficulty);
  const parts = level === null ? [] : composition(level, props.merged);
  /*
    Слова про уровни живут в `features/exercises/topics/one-topic-text.ts`: те же
    строки читает выбор одной темы в комнате урока, а вторая копия перечисления
    разъехалась бы на первой же правке союза
  */
  const only = topic === null ? null : onlyLevelsNote(available);
  const problem = props.problem === null ? null : rowProblemText(props.problem, props.place);

  return (
    <li className="picks__row">
      <div className="picks__controls">
        <select
          className="fld"
          value={row.skill}
          aria-label="Тема"
          disabled={props.busy}
          onChange={(e) => props.onSkill(e.target.value)}
        >
          <option value="">выберите тему</option>
          {topicsBySection(props.topics).map((group) => (
            <optgroup key={group.section} label={group.sectionName}>
              {group.topics.map((t) => (
                <option key={t.skill} value={t.skill}>
                  {t.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {/*
          ⚠️ У темы с единственным уровнем переключателя НЕТ ВОВСЕ, вместо него
          строка. Три сегмента, из которых два выключены, это шум, притворяющийся
          выбором: реальный выбор уровня есть у четырёх тем из одиннадцати.
          Уровень при этом всё равно уезжает на сервер, контракт не меняется.

          ⚠️ Слова «уровень» в этой строке больше НЕТ, и это не вкус. Строкой
          ниже стоит уровень материала («Материал: уровень B1»), а это другая
          шкала: `past-perfect` это B1, но задание по нему бывает только
          сложное. Два «уровня» в соседних строках читались бы как один, и
          случай не редкий: единственный уровень как раз у `past-perfect`, то
          есть у одной из трёх тем первой английской волны.
        */}
        {topic !== null && available.length === 1 && (
          <span className="picks__single">{singleLevelLine(available[0] as number)}</span>
        )}

        {topic !== null && available.length > 1 && (
          <LevelPicker
            difficulty={row.difficulty}
            available={available}
            busy={props.busy}
            onDifficulty={props.onDifficulty}
          />
        )}

        {/*
          Количество и крестик одной парой: на узкой ширине ручки переносятся, и
          без обёртки пара рвалась бы порознь, а разорванные «сколько» и
          «убрать» читаются хуже всего. Пара прижата вправо (`.picks__tail`)
        */}
        <span className="picks__tail">
          <input
            type="number"
            className="fld picks__count"
            aria-label="Сколько заданий"
            min={1}
            max={props.max}
            value={row.count}
            disabled={props.busy || level === null}
            onChange={(e) => props.onCount(Number(e.target.value))}
          />

          {/*
            Тихая кнопка-значок: крестик «убрать тему» это отказ от строки, а не
            действие, ради которого строку завели, и коробки ему не надо
          */}
          <button
            type="button"
            className="btn btn--ico btn--quiet"
            aria-label="Убрать тему"
            disabled={props.busy || !props.removable}
            onClick={props.onRemove}
          >
            ✕
          </button>
        </span>
      </div>

      {/*
        Уровень МАТЕРИАЛА темы: у математики «Материал: 8 класс», у английского
        «Материал: уровень A2». Строка новая, поле `grade` в справочнике было и
        раньше, но форма его не рисовала нигде.

        ⚠️ Стоит отдельной строкой ПОД строкой ручек, а не рядом с
        переключателем сложности, и это не про тесноту. У английского обе шкалы
        называются словом «уровень», а совпадают они не больше, чем у
        математики: `past-simple` это A1, но задание среднего уровня по нему
        труднее лёгкого по `past-perfect` (B1). Соседство двух «уровней» в одной
        строке читалось бы как одна шкала (раздел 3 проекта английского).
      */}
      {topic !== null && (
        <p className="muted picks__note">{materialLine(props.subject, topic.grade)}</p>
      )}

      {only !== null && <p className="muted picks__note">{only}</p>}

      {parts.length > 0 && (
        <p className="muted picks__note">
          {compositionLine(parts, props.merged, props.merged !== row.count)}
        </p>
      )}
      {parts.length > 0 && <p className="muted picks__note">{answerLine(parts)}</p>}

      {/* Текст ошибки стоит У СТРОКИ, а не под кнопкой: под кнопкой человек не
          поймёт, какая из трёх тем виновата. `null` у пустой строки и у
          перебора общего потолка на уроке, разбор в `topic-picks-text.ts` */}
      {problem !== null && <p className="err picks__note">{problem}</p>}
    </li>
  );
}
